import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';

import ValidationAlert from '../../../app/components/ValidationAlert';
import { CreateUpdateAuthorizedDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import { useNotifications } from '../../../common/hooks/useNotifications';
import LastEditorInfo from '../../../app/components/LastEditorInfo';
import { usePokCore } from '../../../common/hooks/usePokCore';
import Button from '../../../common/components/Button/Button';
import { useTranslation } from '../../../common/hooks/useTranslation';

import { AuthorizedEditorForm } from './AuthorizedEditorForm';

interface AuthorizedModalEditorProps {
  id?: string;
  systemNumber?: number;
  authorized: CreateUpdateAuthorizedDto;
  handleClose?: () => void;
  show?: boolean;
  onCompleted: (
    authorized: CreateUpdateAuthorizedDto,
    id?: string,
  ) => Promise<{
    saved: boolean;
    errors: string[];
  }>;
}

export const AuthorizedModalEditor: React.FC<
  AuthorizedModalEditorProps
> = props => {
  const [authorized, setAuthorized] = useState(props.authorized);
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const pok = usePokCore();
  const notifications = useNotifications();
  const { t, tk } = useTranslation('administrationLng');

  useEffect(() => {
    if (props.show) {
      setSaveErrors([]);
      setShowSaveErrors(false);
      setAuthorized(props.authorized);
    }
  }, [props.authorized, props.show]);

  const createAuthorized = async () => {
    setIsLoading(true);
    props
      .onCompleted(authorized, props.id)
      .then(saveStatus => {
        if (!saveStatus.saved) {
          setSaveErrors(saveStatus.errors);
          setShowSaveErrors(true);
          return;
        }
        notifications.saveCompleted();
      })
      .catch(errorResponse => {
        notifications.caughtError(errorResponse);
      })
      .finally(() => setIsLoading(false));
  };

  const propertyChange = (obj: Partial<CreateUpdateAuthorizedDto>) => {
    setAuthorized({ ...authorized, ...obj });
  };

  return (
    <>
      <Modal
        onHide={props.handleClose}
        show={props.show}
        centered={true}
        keyboard={true}
        backdrop="static"
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {props.id
              ? `${t(tk.administration.authorizedEdit)} ${props.systemNumber}`
              : t(tk.administration.authorizedAdd)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <AuthorizedEditorForm
            authorized={authorized}
            propertyChange={propertyChange}
            creatingAuthorized={props.id === undefined}
          />
          <ValidationAlert
            show={showSaveErrors}
            errors={saveErrors}
            className="m-3"
          />
        </Modal.Body>
        <Modal.Footer>
          {props.id && (
            <LastEditorInfo
              id={props.id}
              method={pok.authorized.getLastEditor}
            />
          )}
          <Button
            variant="outline-secondary"
            onClick={props.handleClose}
            disabled={isLoading}
          >
            {t(tk.button.close)}
          </Button>
          <Button
            variant="primary"
            onClick={createAuthorized}
            isLoading={isLoading}
          >
            {t(tk.button.save)}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
